import React, { useMemo } from "react";

import { RouteChildrenProps } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmojiFlagsIcon from "@mui/icons-material/EmojiFlags";
import { makeStyles } from "tss-react/mui";

import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { Offer } from "api/onboardingTypes";
import { BlockStandard } from "components/admin/BlockStandard";
import { DataTable, DataTableProps } from "components/dashboard/DataTable";
import { useGreenEnergyContext } from "contexts/GreenEnergyContext";

const useStyles = makeStyles()((theme) => ({
  accordion: {
    boxShadow: "none",
    marginTop: theme.spacing(2),
    maxWidth: "100%",
    "& > div": {
      padding: 0,
    },
    "& img": {
      marginRight: theme.spacing(1),
    },
  },
  collapseGrid: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "1fr 4fr",
    gridTemplateRows: "repeat(3, 1fr)",
    alignItems: "center",

    "& > span": {
      gridColumn: "2 / 3",
    },

    "& > img": {
      gridColumn: "1 / 2",
      width: "100%",
    },
  },
}));

export const OfferDetail = ({ location, history }: RouteChildrenProps) => {
  const { classes } = useStyles();
  const { setOfferId } = useGreenEnergyContext();

  const offer = useMemo(
    // @ts-expect-error offer is custom and not typed by react-router
    () => (location.state?.offer as Offer) || {},
    [location]
  );

  const handlesubmit = () => {
    setOfferId(offer.contractId);
    history.push("/onboarding/contract-form");
  };

  const powerPlantData = useMemo(() => {
    const data: DataTableProps["data"] = [];
    if (offer.aboutThePowerPlant["source"]) {
      data.push({
        key: "Fonte",
        value: offer.aboutThePowerPlant["source"],
      });
    }

    if (offer.aboutThePowerPlant.installedPower) {
      data.push({
        key: "Potência instalada",
        value: offer.aboutThePowerPlant.installedPower,
      });
    }

    if (offer.aboutThePowerPlant.location) {
      data.push({
        key: "Localização",
        value: offer.aboutThePowerPlant.location,
      });
    }
    if (offer.aboutThePowerPlant.employees) {
      data.push({
        key: "Número de colaboradores",
        value: `${offer.aboutThePowerPlant.employees}`,
      });
    }
    return data;
  }, [offer]);

  return (
    <SolutionsFrame title="Contrato de energia verde">
      <img
        alt=""
        src={offer.image}
        style={{ width: "100%", borderRadius: "8px" }}
      />
      <Grid item container direction="column" sx={{ mt: 3, mb: 2 }}>
        <Grid item container justifyContent="space-between" alignItems="center">
          <Typography variant="h1" sx={{ m: 0 }}>
            {offer.name}
          </Typography>
          <img
            style={{ margin: 0, height: "40px" }}
            src={offer.smallLogo}
            alt=""
          />
        </Grid>

        <Grid item container direction="column" sx={{ mt: 1 }}>
          <Accordion className={classes.accordion} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img src="/static/img/icons/icon-file-check.svg" alt="" />
              <Typography variant="h2" component="span">
                Descrição da oferta
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <BlockStandard hideDivider>
                <Typography>{offer.description}</Typography>
              </BlockStandard>

              <BlockStandard title="Desconto por bandeira" hideDivider>
                <DataTable
                  detailTable
                  data={[
                    {
                      key: "Vermelha 2",
                      value: `${offer.descount.redFlag2} %`,
                      StartIcon: (
                        <EmojiFlagsIcon
                          color="inherit"
                          sx={{ color: (theme) => theme.palette.error.dark }}
                        />
                      ),
                    },
                    {
                      key: "Vermelha 1",
                      value: `${offer.descount.redFlag1} %`,
                      StartIcon: <EmojiFlagsIcon color="error" />,
                    },
                    {
                      key: "Amarela",
                      value: `${offer.descount.yellowFlag1} %`,
                      StartIcon: <EmojiFlagsIcon color="warning" />,
                    },
                    {
                      key: "Verde",
                      value: `${offer.descount.greenFlag1} %`,
                      StartIcon: <EmojiFlagsIcon color="success" />,
                    },
                  ]}
                />
              </BlockStandard>

              <BlockStandard title="Demais condições" hideDivider>
                <DataTable
                  detailTable
                  data={[
                    {
                      key: "Previsão do início do desconto",
                      value: `${offer.descount.startDays} dias`,
                    },
                    {
                      key: "Prazo mínimo de contrato",
                      value: `${offer.descount.minContractDays}`,
                    },
                    {
                      key: "Cancelamento",
                      value: `${offer.descount.cancelation} dias`,
                    },
                    {
                      key: "Consumo mínimo médio mensal",
                      value: `${offer.descount.cancelation} kWh`,
                    },
                  ]}
                />
              </BlockStandard>
            </AccordionDetails>
          </Accordion>

          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img src="/static/img/icons/icon-solar-panel.svg" alt="" />
              <Typography variant="h2" component="span">
                Sobre a usina geradora
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <BlockStandard hideDivider>
                <DataTable detailTable data={powerPlantData} />
              </BlockStandard>

              {!!offer.aboutThePowerPlant.employeeInformation.length && (
                <BlockStandard hideDivider>
                  <Card>
                    <CardContent>
                      <Typography variant="body2">
                        Conheça a equipe local
                      </Typography>
                      <Grid item container direction="row" sx={{ py: 1 }}>
                        {offer.aboutThePowerPlant.employeeInformation.map(
                          ({ image, name, role }) => (
                            <Grid
                              item
                              container
                              direction="column"
                              alignItems="center"
                              sx={{ flex: 1 }}
                              gap={0.5}
                            >
                              <img style={{ margin: 0 }} src={image} alt="" />
                              <Typography variant="caption" fontWeight="bold">
                                {name}
                              </Typography>
                              <Typography variant="caption" align="center">
                                {role}
                              </Typography>
                            </Grid>
                          )
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </BlockStandard>
              )}

              {offer.aboutThePowerPlant.tour && (
                <BlockStandard hideDivider>
                  <Card>
                    <CardContent>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Tour virtual
                      </Typography>
                      <iframe
                        style={{ width: "100%" }}
                        src={offer.aboutThePowerPlant.tour}
                        title="tour"
                      />
                    </CardContent>
                  </Card>
                </BlockStandard>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <img
                style={{ margin: 0 }}
                src="/static/menuIcons/monitoring.svg"
                alt=""
              />
              <Typography variant="h2" component="span">
                Sobre o fornecedor de energia
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                item
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="body2"> Fornecedor</Typography>
                <img
                  style={{ margin: 0, height: "40px" }}
                  src={offer.supplier.logoSupplier}
                  alt=""
                />
              </Grid>
              <Typography>{offer.supplier.about}</Typography>

              {!!offer.supplier.video && (
                <BlockStandard hideDivider>
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <iframe
                        height="350px"
                        width="100%"
                        src={offer.supplier.video}
                        title="sobre o fornecedor"
                      />
                    </CardContent>
                  </Card>
                </BlockStandard>
              )}

              {!!offer.supplier.employeeInformation.length && (
                <BlockStandard hideDivider>
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="body2">
                        Conheça o nosso time
                      </Typography>
                      <Grid item container direction="row" sx={{ py: 1 }}>
                        <Grid item container direction="row" sx={{ py: 1 }}>
                          {offer.supplier?.employeeInformation &&
                            offer.supplier.employeeInformation.map(
                              ({ image, name, role }) => (
                                <Grid
                                  item
                                  container
                                  direction="column"
                                  alignItems="center"
                                  sx={{ flex: 1 }}
                                  gap={0.5}
                                >
                                  <img
                                    style={{ margin: 0 }}
                                    src={image}
                                    alt=""
                                  />
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {name}
                                  </Typography>
                                  <Typography variant="caption" align="center">
                                    {role}
                                  </Typography>
                                </Grid>
                              )
                            )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </BlockStandard>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Button
          sx={{ mt: 2 }}
          fullWidth
          variant="contained"
          onClick={handlesubmit}
        >
          Contratar
        </Button>
      </Grid>
    </SolutionsFrame>
  );
};
